import BaseRangeDatepicker from 'components/atoms/base-range-datepicker';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import BaseWrapper from 'components/atoms/base-wrapper';
import { CommentType, ModuleType } from 'infrastructure/enums';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { endOfDate, startOfDate } from 'infrastructure/functions';
import GeneralCommentsIcon from 'components/atoms/icons/generalCommentsIcon';
import useUser from 'utils/useUser';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import classNames from 'classnames';
import { useComment } from 'infrastructure/hooks/comments';
import { sortArrayByFieldName } from 'utils/helpers/sort-array-by-field-name';

import GeneralCommentsList from './list/index';
import s from './styles.module.scss';
import GeneralCommentsTextArea from './text-area';
import GeneralCommentsEmptyBlock from './empty-block';
import ModuleTabs from '../module-tabs';

import type { Comment } from 'infrastructure/classes/comments/comment';
import type { ReactNode } from 'react';
import type { DateRange } from 'infrastructure/types';
import type { IGeneralCommentsTextAreaRef } from './text-area';

export type GeneralCommentsMode = 'single' | 'split';

interface IGeneralCommentsProps {
  patientGuid: string;
  className?: string;
  readOnly?: boolean;
  module?: ModuleType;
  interactionStatus?: boolean;
  withSwitcher?: boolean;
  switchModeButton?: ReactNode;
  mode?: GeneralCommentsMode;
  onTabChange?: (m: ModuleType) => void;
  isAdminPanel?: boolean;
}

export interface CommentSectionConfig {
  desktopHeight: number;
  mobileHeight: number;
  bottomPadding: number;
}

export const DEFAULT_CONFIG: CommentSectionConfig = {
  desktopHeight: 609,
  mobileHeight: 409,
  bottomPadding: 74,
};

const GeneralComments: React.FC<IGeneralCommentsProps> = ({
  patientGuid,
  interactionStatus = false,
  module: type = ModuleType.RPM,
  readOnly = false,
  withSwitcher = false,
  switchModeButton,
  className,
  mode,
  onTabChange,
  isAdminPanel,
}) => {
  const { t } = useTranslation();
  const { userType, hasClinicalRole, isMobileDevice } = useUser();
  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({
      patientGuid,
    });
  const { updateInteractionStatus } = useUpdateInteractionStatus();

  const [module, setModule] = useState<ModuleType>(type);
  const [withFilters, setWithFilters] = useState(false);
  const [bottomPadding, setBottomPadding] = useState(
    DEFAULT_CONFIG.bottomPadding,
  );

  const generalCommentsTextAreaRef = useRef<IGeneralCommentsTextAreaRef>(null);
  const isSameModule = mode ? true : type === module;

  const {
    data,
    setMeta,
    sendComment,
    deleteComment,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isSending,
    isDeleting,
  } = useComment({
    patientGuid,
    meta: { commentType: module },
  });

  const comments = useMemo(() => {
    const rows =
      data?.pages
        .flatMap((el) => el.data)
        .filter(
          (el) => el.patientGuid === patientGuid && el.commentType === module,
        ) || [];
    return sortArrayByFieldName(rows, 'createdAt');
  }, [data, patientGuid, module]);

  const { desktopHeight, mobileHeight } = DEFAULT_CONFIG;
  const commentsHeight = isMobileDevice ? mobileHeight : desktopHeight;
  const title = `${t(
    `labels.${
      module as Exclude<ModuleType, ModuleType.RPMCCM | ModuleType.APCM>
    }`,
  )} ${t('labels.generalComments')}`;
  const textAreaLoading = isLoading || isSending || isDeleting;

  const handleSendComment = useCallback(
    async (message: string) => {
      if (!message.trim()) return;
      let interactionConfirm = false;

      try {
        if (
          hasClinicalRole &&
          module === ModuleType.RPM &&
          !interactionStatus
        ) {
          interactionConfirm = await showMonthlyInteractionAlert();
        }

        const response = await sendComment({
          commentType: module,
          message,
          patientGuid,
          type: CommentType.Text,
          actionFrom: isAdminPanel ? 'admin-panel' : undefined,
        });

        if (response && interactionConfirm) {
          await updateInteractionStatus({ patientGuid, isInteracted: true });
        }
      } finally {
        generalCommentsTextAreaRef.current?.resetForm();
      }
    },
    [
      module,
      patientGuid,
      interactionStatus,
      hasClinicalRole,
      showMonthlyInteractionAlert,
      sendComment,
      updateInteractionStatus,
      t,
    ],
  );

  const handleDeleteComment = useCallback(
    async (commentToDelete: Comment) => {
      await deleteComment({
        guid: commentToDelete.guid,
        userType,
        patientGuid: commentToDelete.patientGuid,
        commentType: module,
        type: commentToDelete.type as CommentType,
      });
    },
    [deleteComment, module, patientGuid, userType],
  );

  const handleTimeRangeChange = async (range?: DateRange) => {
    const parsedRange: [number, number] | undefined = range
      ? [startOfDate(range[0]).getTime(), endOfDate(range[1]).getTime()]
      : undefined;

    const [startTime, endTime] = parsedRange || [];

    setMeta((prev) => ({
      ...prev,
      startTime,
      endTime,
    }));
    setWithFilters(!!range);
  };

  const handleModuleChange = useCallback(
    (m: ModuleType) => {
      setModule(m);
      setMeta((prev) => ({ ...prev, commentType: m }));
      onTabChange?.(m);
    },
    [onTabChange, setMeta],
  );

  useEffect(() => {
    if (type !== module) handleModuleChange(type);
  }, [type]);

  return (
    <BaseSpinWrapper spinning={isLoading}>
      <BaseWrapper className={classNames(s.wrapper, className)}>
        <div className={s.header} data-cy="general-comments-list">
          <p className={s.title} data-cy={`general-comment-title-${module}`}>
            <GeneralCommentsIcon />
            {title}
          </p>
          <div className={s.controls}>
            <BaseRangeDatepicker withPresets onChange={handleTimeRangeChange} />
            {withSwitcher && (
              <ModuleTabs
                activeKey={module}
                onChange={({ key }) => handleModuleChange(key)}
              />
            )}
            {switchModeButton}
          </div>
        </div>
        <div className={s.body}>
          {comments.length ? (
            <GeneralCommentsList
              id={`general-comments-list${module}`}
              items={comments}
              hasMore={hasNextPage}
              loadNext={fetchNextPage}
              height={commentsHeight - bottomPadding}
              onDeleteComment={handleDeleteComment}
            />
          ) : (
            <GeneralCommentsEmptyBlock
              withFilters={withFilters}
              height={commentsHeight - bottomPadding}
            />
          )}
        </div>
        {!readOnly && isSameModule && (
          <div className={s.footer} data-cy={`general-comment-input-${module}`}>
            <GeneralCommentsTextArea
              type={module}
              ref={generalCommentsTextAreaRef}
              loading={textAreaLoading}
              onSend={handleSendComment}
              onResize={({ height }) => setBottomPadding(height)}
            />
          </div>
        )}
      </BaseWrapper>
      {MonthlyInteractionAlert}
    </BaseSpinWrapper>
  );
};

export default GeneralComments;
